import React, { useEffect, useMemo, useState } from 'react';

import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

import {
  buildModifyAmendmentDrawerSections,
  IModifyAmendmentFormValues
} from '@common/constants/drawer.constants';
import { useModifyAmendment } from '@common/slices/drawer.slice';

import { IBaseDrawerProps } from '@common/types';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';

interface Props extends IBaseDrawerProps {
  amendmentId: string;
  amendmentType: string;
  systemReferenceDate: string;
  policyEffectiveDate: string;
  policyExpiryDate: string;
  initialValues: {
    amendmentReason: string;
    amendmentIssueDate: string;
    amendmentEffectiveDate: string;
  };
  isRedBridgeTravel?: boolean;
}

const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const ModifyAmendmentDrawer: React.FC<Props> = ({
  onSuccess,
  amendmentId,
  amendmentType,
  systemReferenceDate,
  policyEffectiveDate,
  policyExpiryDate,
  initialValues
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [modifyAmendment] = useModifyAmendment();

  const [values, setValues] =
    useState<IModifyAmendmentFormValues>(initialValues);
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  // Determine field visibility and labels based on amendment type
  const showReason = useMemo(() => {
    return amendmentType === 'PolicyCancellation';
  }, [amendmentType]);

  const issueDateLabel = useMemo(() => {
    return amendmentType === 'PolicyEndorsement'
      ? 'Amendment Issue Date*'
      : 'Cancellation Issue Date*';
  }, [amendmentType]);

  const effectiveDateLabel = useMemo(() => {
    return amendmentType === 'PolicyEndorsement'
      ? 'Amendment Effective Date*'
      : 'Cancellation Effective Date*';
  }, [amendmentType]);

  // Calculate min/max dates based on system reference date and policy dates
  const { minDate, maxDate } = useMemo(() => {
    if (!systemReferenceDate) {
      const policyEff = policyEffectiveDate
        ? new Date(policyEffectiveDate)
        : undefined;
      const policyExp = policyExpiryDate
        ? new Date(policyExpiryDate)
        : undefined;

      return {
        minDate: policyEff,
        maxDate: policyExp
      };
    }

    const sysRefDate = new Date(systemReferenceDate);
    const policyEffDate = policyEffectiveDate
      ? new Date(policyEffectiveDate)
      : null;
    const policyExpDate = policyExpiryDate ? new Date(policyExpiryDate) : null;

    const baseMin = addMonths(sysRefDate, -3);
    const baseMax = addMonths(sysRefDate, 1);

    let calculatedMinDate = baseMin;
    if (policyEffDate && policyEffDate > calculatedMinDate) {
      calculatedMinDate = policyEffDate;
    }

    let calculatedMaxDate = baseMax;
    if (policyExpDate && policyExpDate < calculatedMaxDate) {
      calculatedMaxDate = policyExpDate;
    }

    return {
      minDate: calculatedMinDate,
      maxDate: calculatedMaxDate
    };
  }, [systemReferenceDate, policyEffectiveDate, policyExpiryDate]);

  // Build sections when values or configuration changes
  useEffect(() => {
    const newSections = buildModifyAmendmentDrawerSections({
      showReason,
      amendmentReason: values.amendmentReason,
      amendmentIssueDate: values.amendmentIssueDate,
      amendmentEffectiveDate: values.amendmentEffectiveDate,
      minDate,
      maxDate,
      issueDateLabel,
      effectiveDateLabel
    });
    setSections(newSections);
  }, [
    values,
    showReason,
    minDate,
    maxDate,
    issueDateLabel,
    effectiveDateLabel
  ]);

  const onSubmit = async (formValues: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(sections, formValues);

    const res = await modifyAmendment({
      input: {
        amendmentID: amendmentId,
        amendmentReason: data.amendmentReason || ''
      }
    });

    if (!res.errors) {
      onSuccess();
    }
  };

  const handleChange = (
    fieldName: keyof IModifyAmendmentFormValues,
    value: any,
    _sectionIndex: number,
    updatedValues: IModifyAmendmentFormValues
  ) => {
    setValues(updatedValues);
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={onSubmit}
      submitButtonState={submitButtonState}
      onChange={handleChange}
    />
  );
};

export { ModifyAmendmentDrawer };
